import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../components/Layout';
import api from '../components/api';
import { 
  Loader2, Save, ArrowLeft, Upload, 
  Building2, User, Box, Shield, Users2, 
  FileText, Truck, ClipboardCheck 
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const EditDepotPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    // Depot
    consent: false,
    motif_refus: '',
    region: '',
    departement: '',
    commune: '',
    commune_autre: '',
    localite: '',
    quartier: '',
    localisation_usuelle: '',
    gps: '',
    autres_informations: '',
    formation_sanitaire_exists: false,
    officine_exists: false,

    // DepotInfo
    nom_depot: '',
    date_creation: '',
    date_ouverture: '',
    nombre_depots: '',
    plusieurs_autorisations: false,
    numero_autorisation: '',
    autorisation_delivree_par: '',
    autorisation_delivree_par_autre: '',
    officine_parraine: false,
    nom_officine_marraine: '',
    nom_pharmacien_marraine: '',
    telephone_officine_marraine: '',
    approvisionnement_par_marraine: false,
    type_vente: '',
    type_vente_autre: '',
    statut_depot: '',
    localisation_officielle: '',
    localisation_reelle: '',
    rccm: '',
    nif: '',
    cnss: '',

    // ProprietaireInfo
    statut_repondant: '',
    nom_proprietaire: '',
    genre_proprietaire: '',
    nationalite_proprietaire: '',
    nationalite_proprietaire_autre: '',
    residence_proprietaire: '',
    travail_etat: false,
    travail_prive: false,
    telephone1_proprietaire: '',
    telephone2_proprietaire: '',
    email_proprietaire: '',
    niveau_etudes: '',
    type_diplome: '',

    // GerantInfo
    nom_gerant: '',
    statut_gerant: '',
    telephone1_gerant: '',
    telephone2_gerant: '',
    email_gerant: '',
    niveau_etudes_gerant: '',
    type_formation: '',

    // Infrastructure
    surface_totale: '',
    statut_batiment: '',
    nature_materiaux: '',
    presence_mineur: false,
    climatisation: false,
    refrigerateur: false,
    source_energie: '',
    source_energie_autre: '',

    // DocumentationVisibilite
    has_plaque: false,
    plaque_infos: {},
    autorisation_pdf: null,
    devanture: null,

    // ConformiteReglementaire
    has_supervision: false,
    date_supervision: '',
    resultat_supervision: '',
    actions_correctives_supervision: '',

    // GestionStock
    has_systeme_gestion: false,
    type_systeme_gestion: '',
    has_registre_vente: false,
    has_reporting_vente: false,
    has_fiche_stocks: false,
    has_methode_rangement: false,
    type_methode_rangement: '',
    frequence_inventaire: '',

    // HygieneSecurite
    has_extincteur: false,
    has_detecteur_fume: false,

    // AireStockage
    aire_reception: false,
    aire_stockage: false,
    aire_produits_sensibles: false,
    aire_produits_perimes: false,

    // ClientDepot
    clients_physiques: '',
    centres_sante: '',
    depots_pharmaceutiques: '',
    hopitaux: '',
    cliniques: '',
    officines: '',
    autres: '',

    // RessourcesHumaines
    total_agents: '',
    agents_permanents: '',
    port_insignes: false,

    // NormeProcedure
    achat_exclusif_autorise: false,
    connaissance_arrete: false,
    application_arrete: false,

    // Fournisseurs (tableau)
    fournisseurs: [],
  });

  useEffect(() => {
    fetchDepotData();
  }, [id]);

  const fetchDepotData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`collect/details/${id}/`);
      const data = response.data;
      
      console.log('Data received:', data);  // Pour déboguer
      
      setFormData({
        ...data,
        // Assurez-vous que les données imbriquées sont correctement extraites
        ...data.depotinfo,
        ...data.proprietaireinfo,
        ...data.gerantinfo,
        ...data.infrastructure,
        ...data.documentationvisibilite,
        ...data.conformitereglementaire,
        ...data.gestionstock,
        ...data.hygienesecurite,
        ...data.airestockage,
        ...data.clientdepot,
        ...data.ressourceshumaines,
        ...data.normeprocedure,
        // Données spécifiques qui nécessitent un traitement particulier
        niveau_etudes: data.proprietaireinfo?.niveau_etudes,
        niveau_etudes_gerant: data.gerantinfo?.niveau_etudes,
        frequence_inventaire: data.gestionstock?.frequence_inventaire,
        fournisseurs: data.fournisseurs || [],
      });
    } catch (error) {
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      // Restructurer les données pour correspondre au format attendu par le backend
      const dataToSend = {
        // Données de base du dépôt
        consent: formData.consent,
        motif_refus: formData.motif_refus,
        region: formData.region,
        departement: formData.departement,
        commune: formData.commune,
        commune_autre: formData.commune_autre,
        localite: formData.localite,
        quartier: formData.quartier,
        localisation_usuelle: formData.localisation_usuelle,
        gps: formData.gps,
        autres_informations: formData.autres_informations,
        formation_sanitaire_exists: formData.formation_sanitaire_exists,
        officine_exists: formData.officine_exists,

        // Données structurées pour les relations
        depotinfo: {
          nom_depot: formData.nom_depot,
          date_creation: formData.date_creation,
          date_ouverture: formData.date_ouverture,
          nombre_depots: formData.nombre_depots,
          plusieurs_autorisations: formData.plusieurs_autorisations,
          numero_autorisation: formData.numero_autorisation,
          autorisation_delivree_par: formData.autorisation_delivree_par,
          autorisation_delivree_par_autre: formData.autorisation_delivree_par_autre,
          officine_parraine: formData.officine_parraine,
          nom_officine_marraine: formData.nom_officine_marraine,
          nom_pharmacien_marraine: formData.nom_pharmacien_marraine,
          telephone_officine_marraine: formData.telephone_officine_marraine,
          approvisionnement_par_marraine: formData.approvisionnement_par_marraine,
          type_vente: formData.type_vente,
          type_vente_autre: formData.type_vente_autre,
          statut_depot: formData.statut_depot,
          localisation_officielle: formData.localisation_officielle,
          localisation_reelle: formData.localisation_reelle,
          rccm: formData.rccm,
          nif: formData.nif,
          cnss: formData.cnss,
        },
        
        proprietaireinfo: {
          statut_repondant: formData.statut_repondant,
          nom_proprietaire: formData.nom_proprietaire,
          genre_proprietaire: formData.genre_proprietaire,
          nationalite_proprietaire: formData.nationalite_proprietaire,
          nationalite_proprietaire_autre: formData.nationalite_proprietaire_autre,
          residence_proprietaire: formData.residence_proprietaire,
          travail_etat: formData.travail_etat,
          travail_prive: formData.travail_prive,
          telephone1: formData.telephone1_proprietaire,
          telephone2: formData.telephone2_proprietaire,
          email: formData.email_proprietaire,
          niveau_etudes: formData.niveau_etudes,
          type_diplome: formData.type_diplome,
        },
        
        gerantinfo: {
          nom_gerant: formData.nom_gerant,
          statut_gerant: formData.statut_gerant,
          telephone1: formData.telephone1_gerant,
          telephone2: formData.telephone2_gerant,
          email: formData.email_gerant,
          niveau_etudes_gerant: formData.niveau_etudes_gerant,
          type_formation: formData.type_formation,
        },
        
        fournisseurs: formData.fournisseurs,

        // Infrastructure
        infrastructure: {
          surface_totale: formData.surface_totale,
          statut_batiment: formData.statut_batiment,
          nature_materiaux: formData.nature_materiaux,
          presence_mineur: formData.presence_mineur,
          climatisation: formData.climatisation,
          refrigerateur: formData.refrigerateur,
          source_energie: formData.source_energie,
          source_energie_autre: formData.source_energie_autre,
        },
        documentationvisibilite: {
          has_plaque: formData.has_plaque,
          plaque_infos: formData.plaque_infos,
        },
        conformitereglementaire: {
          has_supervision: formData.has_supervision,
          date_supervision: formData.date_supervision,
          resultat_supervision: formData.resultat_supervision,
          actions_correctives_supervision: formData.actions_correctives_supervision,
        },
        gestionstock: {
          has_systeme_gestion: formData.has_systeme_gestion,
          type_systeme_gestion: formData.type_systeme_gestion,
          has_registre_vente: formData.has_registre_vente,
          has_reporting_vente: formData.has_reporting_vente,
          has_fiche_stocks: formData.has_fiche_stocks,
          has_methode_rangement: formData.has_methode_rangement,
          type_methode_rangement: formData.type_methode_rangement,
          frequence_inventaire: formData.frequence_inventaire,
        },
        hygienesecurite: {
          has_extincteur: formData.has_extincteur,
          has_detecteur_fume: formData.has_detecteur_fume,
        },
        airestockage: {
          aire_reception: formData.aire_reception,
          aire_stockage: formData.aire_stockage,
          aire_produits_sensibles: formData.aire_produits_sensibles,
          aire_produits_perimes: formData.aire_produits_perimes,
        },
        clientdepot: {
          clients_physiques: formData.clients_physiques,
          centres_sante: formData.centres_sante,
          depots_pharmaceutiques: formData.depots_pharmaceutiques,
          hopitaux: formData.hopitaux,
          cliniques: formData.cliniques,
          officines: formData.officines,
          autres: formData.autres,
        },
        ressourceshumaines: {
          total_agents: formData.total_agents,
          agents_permanents: formData.agents_permanents,
          port_insignes: formData.port_insignes,
        },
        normeprocedure: {
          achat_exclusif_autorise: formData.achat_exclusif_autorise,
          connaissance_arrete: formData.connaissance_arrete,
          application_arrete: formData.application_arrete,
        },
      };

      // Créer un FormData pour gérer les fichiers
      const formDataToSend = new FormData();
      
      // Ajouter les données JSON
      formDataToSend.append('data', JSON.stringify(dataToSend));
      
      // Ajouter les fichiers s'ils existent
      if (formData.autorisation_pdf instanceof File) {
        formDataToSend.append('autorisation_pdf', formData.autorisation_pdf);
      }
      
      if (formData.devanture instanceof File) {
        formDataToSend.append('devanture', formData.devanture);
      }

      const response = await api.put(`/collect/details/${id}/`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      toast.success('Dépôt mis à jour avec succès');
      navigate('/depots');
    } catch (error) {
      console.error('Erreur:', error);
      toast.error(error.response?.data?.error || "Erreur lors de la sauvegarde des modifications");
      setSaving(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : 
              type === 'file' ? files[0] : 
              type === 'number' ? parseFloat(value) || '' :
              value
    }));
  };

  const handleFournisseurChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      fournisseurs: prev.fournisseurs.map((f, i) => 
        i === index ? { ...f, [field]: value } : f
      )
    }));
  };

  const addFournisseur = () => {
    setFormData(prev => ({
      ...prev,
      fournisseurs: [...prev.fournisseurs, {
        type: '',
        nom: '',
        nationalite: '',
        nationalite_autre: '',
        telephone: '',
        adresse: ''
      }]
    }));
  };

  const removeFournisseur = (index) => {
    setFormData(prev => ({
      ...prev,
      fournisseurs: prev.fournisseurs.filter((_, i) => i !== index)
    }));
  };

  useEffect(() => {
    console.log('Form Data Updated:', {
      niveau_etudes_proprietaire: formData.niveau_etudes,
      niveau_etudes_gerant: formData.gerantinfo?.niveau_etudes,
      frequence_inventaire: formData.frequence_inventaire
    });
  }, [formData]);

  useEffect(() => {
    if (formData.proprietaireinfo) {
      console.log('Genre propriétaire:', formData.genre_proprietaire);
      console.log('Nationalité propriétaire:', formData.nationalite_proprietaire);
      console.log('Statut répondant:', formData.statut_repondant);
    }
    if (formData.gerantinfo) {
      console.log('Statut gérant:', formData.statut_gerant);
    }
    if (formData.infrastructure) {
      console.log('Statut bâtiment:', formData.statut_batiment);
      console.log('Nature matériaux:', formData.nature_materiaux);
    }
    if (formData.fournisseurs?.length > 0) {
      console.log('Fournisseurs:', formData.fournisseurs);
    }
  }, [formData]);

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-screen">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        <div className="mb-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/depots')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5" />
              Retour
            </button>
            <h1 className="text-2xl font-bold text-gray-900">
              Modifier le dépôt
            </h1>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 text-red-600 p-4 rounded-lg">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Informations de base */}
          <Section title="Informations de base" icon={Building2}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Input
                label="Nom du dépôt"
                name="nom_depot"
                value={formData.nom_depot}
                onChange={handleChange}
                required
              />
              <Input
                label="Région"
                name="region"
                value={formData.region}
                onChange={handleChange}
                required
              />
              <Input
                label="Département"
                name="departement"
                value={formData.departement}
                onChange={handleChange}
                required
              />
              <Input
                label="Commune"
                name="commune"
                value={formData.commune}
                onChange={handleChange}
              />
              <Input
                label="Localité"
                name="localite"
                value={formData.localite}
                onChange={handleChange}
              />
              <Input
                label="Quartier"
                name="quartier"
                value={formData.quartier}
                onChange={handleChange}
              />
              <Input
                label="GPS"
                name="gps"
                value={formData.gps}
                onChange={handleChange}
              />
              <TextArea
                label="Localisation usuelle"
                name="localisation_usuelle"
                value={formData.localisation_usuelle}
                onChange={handleChange}
              />
            </div>
          </Section>

          {/* Propriétaire */}
          <Section title="Information du propriétaire" icon={User}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Input
                label="Nom du propriétaire"
                name="nom_proprietaire"
                value={formData.nom_proprietaire}
                onChange={handleChange}
              />
              <Select
                label="Genre"
                name="genre_proprietaire"
                value={formData.genre_proprietaire}
                onChange={handleChange}
                options={[
                  { value: 'M', label: 'Masculin' },
                  { value: 'F', label: 'Féminin' }
                ]}
              />
              <Select
                label="Nationalité"
                name="nationalite_proprietaire"
                value={formData.nationalite_proprietaire}
                onChange={handleChange}
                options={[
                  { value: 'Nigérienne', label: 'Nigérienne' },
                  { value: 'Autre', label: 'Autre' }
                ]}
              />
              {formData.nationalite_proprietaire === 'Autre' && (
                <Input
                  label="Préciser la nationalité"
                  name="nationalite_proprietaire_autre"
                  value={formData.nationalite_proprietaire_autre}
                  onChange={handleChange}
                />
              )}
              <Input
                label="Résidence"
                name="residence_proprietaire"
                value={formData.residence_proprietaire}
                onChange={handleChange}
              />
              <Input
                label="Téléphone 1"
                name="telephone1_proprietaire"
                value={formData.telephone1_proprietaire}
                onChange={handleChange}
              />
              <Input
                label="Téléphone 2"
                name="telephone2_proprietaire"
                value={formData.telephone2_proprietaire}
                onChange={handleChange}
              />
              <Input
                type="email"
                label="Email"
                name="email_proprietaire"
                value={formData.email_proprietaire}
                onChange={handleChange}
              />
              <Select
                label="Niveau d'études"
                name="niveau_etudes"
                value={formData.niveau_etudes}
                onChange={handleChange}
                options={[
                  { value: 'Secondaire', label: 'Secondaire' },
                  { value: 'Primaire', label: 'Primaire' },
                  { value: 'Supérieur', label: 'Supérieur' },
                  { value: 'Aucun', label: 'Aucun' }
                ]}
              />
              <Input
                label="Type de diplôme"
                name="type_diplome"
                value={formData.type_diplome}
                onChange={handleChange}
              />
              <Select
                label="Statut du répondant"
                name="statut_repondant"
                value={formData.statut_repondant}
                onChange={handleChange}
                options={[
                  { value: 'Propriétaire-Gérant', label: 'Propriétaire-Gérant' },
                  { value: 'Propriétaire', label: 'Propriétaire' },
                  { value: 'Gérant', label: 'Gérant' }
                ]}
              />
              <Checkbox
                label="Travaille dans le secteur public"
                name="travail_etat"
                checked={formData.travail_etat}
                onChange={handleChange}
              />
              <Checkbox
                label="Travaille dans le secteur privé"
                name="travail_prive"
                checked={formData.travail_prive}
                onChange={handleChange}
              />
            </div>
          </Section>

          {/* Gérant */}
          <Section title="Information du gérant" icon={Users2}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Input
                label="Nom du gérant"
                name="nom_gerant"
                value={formData.nom_gerant}
                onChange={handleChange}
              />
              <Select
                label="Statut du gérant"
                name="statut_gerant"
                value={formData.statut_gerant}
                onChange={handleChange}
                options={[
                  { value: 'Propriétaire', label: 'Propriétaire' },
                  { value: 'Gérant', label: 'Gérant' }
                ]}
              />
              <Input
                label="Téléphone 1"
                name="telephone1_gerant"
                value={formData.telephone1_gerant}
                onChange={handleChange}
              />
              <Input
                label="Téléphone 2"
                name="telephone2_gerant"
                value={formData.telephone2_gerant}
                onChange={handleChange}
              />
              <Input
                type="email"
                label="Email"
                name="email_gerant"
                value={formData.email_gerant}
                onChange={handleChange}
              />
              <Select
                label="Niveau d'études"
                name="niveau_etudes"
                value={formData.gerantinfo?.niveau_etudes}
                onChange={handleChange}
                options={[
                  { value: 'Secondaire', label: 'Secondaire' },
                  { value: 'Primaire', label: 'Primaire' },
                  { value: 'Supérieur', label: 'Supérieur' },
                  { value: 'Aucun', label: 'Aucun' }
                ]}
              />
              <Input
                label="Type de formation"
                name="type_formation"
                value={formData.type_formation}
                onChange={handleChange}
              />
            </div>
          </Section>

          {/* Information du dépôt */}
          <Section title="Information du dépôt" icon={Building2}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Input
                type="date"
                label="Date de création"
                name="date_creation"
                value={formData.date_creation}
                onChange={handleChange}
              />
              <Input
                type="date"
                label="Date d'ouverture"
                name="date_ouverture"
                value={formData.date_ouverture}
                onChange={handleChange}
              />
              <Input
                type="number"
                label="Nombre de dépôts"
                name="nombre_depots"
                value={formData.nombre_depots}
                onChange={handleChange}
              />
              <Input
                label="Numéro d'autorisation"
                name="numero_autorisation"
                value={formData.numero_autorisation}
                onChange={handleChange}
              />
              <Select
                label="Autorisation délivrée par"
                name="autorisation_delivree_par"
                value={formData.autorisation_delivree_par}
                onChange={handleChange}
                options={[
                  { value: 'MSAS', label: 'MSAS' },
                  { value: 'DRM', label: 'DRM' },
                  { value: 'Autre', label: 'Autre' }
                ]}
              />
              {formData.autorisation_delivree_par === 'Autre' && (
                <Input
                  label="Préciser autre"
                  name="autorisation_delivree_par_autre"
                  value={formData.autorisation_delivree_par_autre}
                  onChange={handleChange}
                />
              )}
              <Checkbox
                label="Plusieurs autorisations"
                name="plusieurs_autorisations"
                checked={formData.plusieurs_autorisations}
                onChange={handleChange}
              />
              <Checkbox
                label="Officine parraine"
                name="officine_parraine"
                checked={formData.officine_parraine}
                onChange={handleChange}
              />
              {formData.officine_parraine && (
                <>
                  <Input
                    label="Nom de l'officine marraine"
                    name="nom_officine_marraine"
                    value={formData.nom_officine_marraine}
                    onChange={handleChange}
                  />
                  <Input
                    label="Nom du pharmacien marraine"
                    name="nom_pharmacien_marraine"
                    value={formData.nom_pharmacien_marraine}
                    onChange={handleChange}
                  />
                  <Input
                    label="Téléphone officine marraine"
                    name="telephone_officine_marraine"
                    value={formData.telephone_officine_marraine}
                    onChange={handleChange}
                  />
                  <Checkbox
                    label="Approvisionnement par marraine"
                    name="approvisionnement_par_marraine"
                    checked={formData.approvisionnement_par_marraine}
                    onChange={handleChange}
                  />
                </>
              )}
              <Select
                label="Type de vente"
                name="type_vente"
                value={formData.type_vente}
                onChange={handleChange}
                options={[
                  { value: 'En détail', label: 'En détail' },
                  { value: 'En gros', label: 'En gros' },
                  { value: 'Autre', label: 'Autre' }
                ]}
              />
              {formData.type_vente === 'Autre' && (
                <Input
                  label="Préciser autre type de vente"
                  name="type_vente_autre"
                  value={formData.type_vente_autre}
                  onChange={handleChange}
                />
              )}
              <Input
                label="RCCM"
                name="rccm"
                value={formData.rccm}
                onChange={handleChange}
              />
              <Input
                label="NIF"
                name="nif"
                value={formData.nif}
                onChange={handleChange}
              />
              <Input
                label="CNSS"
                name="cnss"
                value={formData.cnss}
                onChange={handleChange}
              />
            </div>
          </Section>

          {/* Fournisseurs */}
          <Section title="Fournisseurs" icon={Truck}>
            <div className="space-y-4">
              {formData.fournisseurs.map((fournisseur, index) => (
                <div key={index} className="p-4 border rounded-lg space-y-4">
                  <div className="flex justify-between items-center">
                    <h3 className="font-medium">Fournisseur {index + 1}</h3>
                    <button
                      type="button"
                      onClick={() => removeFournisseur(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      Supprimer
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <Select
                      label="Type"
                      name={`fournisseurs.${index}.type`}
                      value={fournisseur.type}
                      onChange={(e) => handleFournisseurChange(index, 'type', e.target.value)}
                      options={[
                        { value: 'Grossiste au marché', label: 'Grossiste au marché' },
                        { value: 'Autre', label: 'Autre' }
                      ]}
                    />
                    <Select
                      label="Nationalité"
                      name={`fournisseurs.${index}.nationalite`}
                      value={fournisseur.nationalite}
                      onChange={(e) => handleFournisseurChange(index, 'nationalite', e.target.value)}
                      options={[
                        { value: 'Nigérienne', label: 'Nigérienne' },
                        { value: 'Autre', label: 'Autre' }
                      ]}
                    />
                    <Input
                      label="Téléphone"
                      value={fournisseur.telephone}
                      onChange={(e) => handleFournisseurChange(index, 'telephone', e.target.value)}
                    />
                    <TextArea
                      label="Adresse"
                      value={fournisseur.adresse}
                      onChange={(e) => handleFournisseurChange(index, 'adresse', e.target.value)}
                    />
                  </div>
                </div>
              ))}
              <button
                type="button"
                onClick={addFournisseur}
                className="flex items-center gap-2 text-emerald-600 hover:text-emerald-700"
              >
                + Ajouter un fournisseur
              </button>
            </div>
          </Section>

          {/* Infrastructure */}
          <Section title="Infrastructure" icon={Building2}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Input
                type="number"
                label="Surface totale (m²)"
                name="surface_totale"
                value={formData.surface_totale}
                onChange={handleChange}
              />
              <Select
                label="Statut du bâtiment"
                name="statut_batiment"
                value={formData.statut_batiment}
                onChange={handleChange}
                options={[
                  { value: 'Mise à disposition', label: 'Mise à disposition' },
                  { value: 'Location', label: 'Location' },
                  { value: 'Propriété', label: 'Propriété' }
                ]}
              />
              <Select
                label="Nature des matériaux"
                name="nature_materiaux"
                value={formData.nature_materiaux}
                onChange={handleChange}
                options={[
                  { value: 'Non définitif', label: 'Non définitif' },
                  { value: 'Définitif', label: 'Définitif' }
                ]}
              />
              <Select
                label="Source d'énergie"
                name="source_energie"
                value={formData.source_energie}
                onChange={handleChange}
                options={[
                  { value: 'Nigelec', label: 'Nigelec' },
                  { value: 'Autre', label: 'Autre' }
                ]}
              />
              {formData.source_energie === 'Autre' && (
                <Input
                  label="Préciser autre source d'énergie"
                  name="source_energie_autre"
                  value={formData.source_energie_autre}
                  onChange={handleChange}
                />
              )}
              <Checkbox
                label="Présence de mineurs"
                name="presence_mineur"
                checked={formData.presence_mineur}
                onChange={handleChange}
              />
              <Checkbox
                label="Climatisation"
                name="climatisation"
                checked={formData.climatisation}
                onChange={handleChange}
              />
              <Checkbox
                label="Réfrigérateur"
                name="refrigerateur"
                checked={formData.refrigerateur}
                onChange={handleChange}
              />
            </div>
          </Section>

          {/* Documentation et Visibilité */}
          <Section title="Documentation et Visibilité" icon={FileText}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Checkbox
                label="Présence de plaque"
                name="has_plaque"
                checked={formData.has_plaque}
                onChange={handleChange}
              />
              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Autorisation (PDF)
                </label>
                <input
                  type="file"
                  name="autorisation_pdf"
                  onChange={handleChange}
                  accept=".pdf"
                  className="w-full"
                />
              </div>
              <div className="col-span-full">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Photo de la devanture
                </label>
                <input
                  type="file"
                  name="devanture"
                  onChange={handleChange}
                  accept="image/*"
                  className="w-full"
                />
              </div>
            </div>
          </Section>

          {/* Conformité Réglementaire */}
          <Section title="Conformité Réglementaire" icon={Shield}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Checkbox
                label="A eu une supervision"
                name="has_supervision"
                checked={formData.has_supervision}
                onChange={handleChange}
              />
              {formData.has_supervision && (
                <>
                  <Input
                    type="date"
                    label="Date de supervision"
                    name="date_supervision"
                    value={formData.date_supervision}
                    onChange={handleChange}
                  />
                  <TextArea
                    label="Résultat de la supervision"
                    name="resultat_supervision"
                    value={formData.resultat_supervision}
                    onChange={handleChange}
                  />
                  <TextArea
                    label="Actions correctives"
                    name="actions_correctives_supervision"
                    value={formData.actions_correctives_supervision}
                    onChange={handleChange}
                  />
                </>
              )}
            </div>
          </Section>

          {/* Gestion de Stock */}
          <Section title="Gestion de Stock" icon={Box}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Checkbox
                label="Système de gestion"
                name="has_systeme_gestion"
                checked={formData.has_systeme_gestion}
                onChange={handleChange}
              />
              {formData.has_systeme_gestion && (
                <Input
                  label="Type de système de gestion"
                  name="type_systeme_gestion"
                  value={formData.type_systeme_gestion}
                  onChange={handleChange}
                />
              )}
              <Checkbox
                label="Registre de vente"
                name="has_registre_vente"
                checked={formData.has_registre_vente}
                onChange={handleChange}
              />
              <Checkbox
                label="Reporting des ventes"
                name="has_reporting_vente"
                checked={formData.has_reporting_vente}
                onChange={handleChange}
              />
              <Checkbox
                label="Fiches de stock"
                name="has_fiche_stocks"
                checked={formData.has_fiche_stocks}
                onChange={handleChange}
              />
              <Checkbox
                label="Méthode de rangement"
                name="has_methode_rangement"
                checked={formData.has_methode_rangement}
                onChange={handleChange}
              />
              {formData.has_methode_rangement && (
                <Input
                  label="Type de méthode de rangement"
                  name="type_methode_rangement"
                  value={formData.type_methode_rangement}
                  onChange={handleChange}
                />
              )}
              <Select
                label="Fréquence d'inventaire"
                name="frequence_inventaire"
                value={formData.frequence_inventaire}
                onChange={handleChange}
                options={[
                  { value: 'Quotidienne', label: 'Quotidienne' },
                  { value: 'Hebdomadaire', label: 'Hebdomadaire' },
                  { value: 'Mensuelle', label: 'Mensuelle' },
                  { value: 'Trimestrielle', label: 'Trimestrielle' },
                  { value: 'Semestrielle', label: 'Semestrielle' },
                  { value: 'Annuelle', label: 'Annuelle' },
                  { value: 'Autre', label: 'Autre' }
                ]}
              />
            </div>
          </Section>

          {/* Hygiène et Sécurité */}
          <Section title="Hygiène et Sécurité" icon={Shield}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Checkbox
                label="Présence d'extincteur"
                name="has_extincteur"
                checked={formData.has_extincteur}
                onChange={handleChange}
              />
              <Checkbox
                label="Détecteur de fumée"
                name="has_detecteur_fume"
                checked={formData.has_detecteur_fume}
                onChange={handleChange}
              />
            </div>
          </Section>

          {/* Aires de Stockage */}
          <Section title="Aires de Stockage" icon={Box}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Checkbox
                label="Aire de réception"
                name="aire_reception"
                checked={formData.aire_reception}
                onChange={handleChange}
              />
              <Checkbox
                label="Aire de stockage"
                name="aire_stockage"
                checked={formData.aire_stockage}
                onChange={handleChange}
              />
              <Checkbox
                label="Aire pour produits sensibles"
                name="aire_produits_sensibles"
                checked={formData.aire_produits_sensibles}
                onChange={handleChange}
              />
              <Checkbox
                label="Aire pour produits périmés"
                name="aire_produits_perimes"
                checked={formData.aire_produits_perimes}
                onChange={handleChange}
              />
            </div>
          </Section>

          {/* Clients */}
          <Section title="Répartition des Clients (%)" icon={Users2}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Input
                type="number"
                label="Clients physiques (%)"
                name="clients_physiques"
                value={formData.clients_physiques}
                onChange={handleChange}
                min="0"
                max="100"
              />
              <Input
                type="number"
                label="Centres de santé (%)"
                name="centres_sante"
                value={formData.centres_sante}
                onChange={handleChange}
                min="0"
                max="100"
              />
              <Input
                type="number"
                label="Dépôts pharmaceutiques (%)"
                name="depots_pharmaceutiques"
                value={formData.depots_pharmaceutiques}
                onChange={handleChange}
                min="0"
                max="100"
              />
              <Input
                type="number"
                label="Hôpitaux (%)"
                name="hopitaux"
                value={formData.hopitaux}
                onChange={handleChange}
                min="0"
                max="100"
              />
              <Input
                type="number"
                label="Cliniques (%)"
                name="cliniques"
                value={formData.cliniques}
                onChange={handleChange}
                min="0"
                max="100"
              />
              <Input
                type="number"
                label="Officines (%)"
                name="officines"
                value={formData.officines}
                onChange={handleChange}
                min="0"
                max="100"
              />
              <Input
                type="number"
                label="Autres (%)"
                name="autres"
                value={formData.autres}
                onChange={handleChange}
                min="0"
                max="100"
              />
            </div>
          </Section>

          {/* Ressources Humaines */}
          <Section title="Ressources Humaines" icon={Users2}>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Input
                type="number"
                label="Total des agents"
                name="total_agents"
                value={formData.total_agents}
                onChange={handleChange}
                min="0"
              />
              <Input
                type="number"
                label="Agents permanents"
                name="agents_permanents"
                value={formData.agents_permanents}
                onChange={handleChange}
                min="0"
              />
              <Checkbox
                label="Port d'insignes"
                name="port_insignes"
                checked={formData.port_insignes}
                onChange={handleChange}
              />
            </div>
          </Section>

          {/* Normes et Procédures */}
          <Section title="Normes et Procédures" icon={ClipboardCheck}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Checkbox
                label="Achat exclusif autorisé"
                name="achat_exclusif_autorise"
                checked={formData.achat_exclusif_autorise}
                onChange={handleChange}
              />
              <Checkbox
                label="Connaissance de l'arrêté"
                name="connaissance_arrete"
                checked={formData.connaissance_arrete}
                onChange={handleChange}
              />
              <Checkbox
                label="Application de l'arrêté"
                name="application_arrete"
                checked={formData.application_arrete}
                onChange={handleChange}
              />
            </div>
          </Section>

          {/* Boutons de soumission */}
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => navigate('/liste_depot')}
              className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save className="h-4 w-4" />
              )}
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

// Composants utilitaires
const Section = ({ title, children, icon: Icon }) => (
  <div className="bg-white rounded-lg shadow p-6">
    <h2 className="text-lg font-semibold text-gray-900 mb-6">{title}</h2>
    {Icon && <Icon className="h-6 w-6 text-emerald-600 mb-4" />}
    {children}
  </div>
);

const Input = ({ label, name, value, onChange, type = 'text', required = false }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      required={required}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
    />
  </div>
);

const Select = ({ label, name, value, onChange, options, required = false }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label} {required && <span className="text-red-500">*</span>}
    </label>
    <select
      name={name}
      value={value || ''}
      onChange={onChange}
      required={required}
      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-emerald-500 focus:ring-emerald-500"
    >
      <option value="">Sélectionner</option>
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  </div>
);

const Checkbox = ({ label, name, checked, onChange }) => (
  <label className="flex items-center gap-2">
    <input
      type="checkbox"
      name={name}
      checked={checked}
      onChange={onChange}
      className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
    />
    <span className="text-sm text-gray-700">{label}</span>
  </label>
);

const TextArea = ({ label, name, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 mb-1">
      {label}
    </label>
    <textarea
      name={name}
      value={value}
      onChange={onChange}
      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
    />
  </div>
);

export default EditDepotPage;
